// src/pages/received-quotation/list.tsx
// Canvas OrdB_Quotes: «Үйлдэл» first column, mono codes, DotPill status, «1-13 / 13» footer.
import { ActionIcon, Anchor, Box, Breadcrumbs, Flex, Pagination, Select, Text, TextInput } from "@mantine/core"
import { EyeIcon, FileTextIcon, MagnifyingGlassIcon } from "@phosphor-icons/react"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router"
import useSWR from "swr"
import { ReceivedQuotationApi } from "../../apis"
import { DotPill } from "../../components/delivery/atoms"
import { TableFrame } from "../../components/delivery/atoms"
import { usePermission } from "../../components/permission"
import { DeniedCard, ListState, ReasonTooltip } from "../../components/ui"
import { ISalesQuotation } from "../../models/Quotation"
import { currencyFormat } from "../../utils"
import { dateFormat } from "../../utils/date"
import { BUYER_QUOTATION_STATUSES, quotationStatusTone } from "../../utils/quotation-status"
import classes from "./list.module.css"

const LIMIT = 20

export function ReceivedQuotationList() {
  const { t } = useTranslation(["quotation", "common"])
  const navigate = useNavigate()
  const LIST = usePermission("PRICING_QUOTATION_LIST")
  const GET = usePermission("PRICING_QUOTATION_GET")
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState<string | null>(null)
  const [search, setSearch] = useState("")

  const fetcher = useCallback(() => {
    const filter: { status?: string; query?: string } = {}
    if (status) filter.status = status
    if (search.trim()) filter.query = search.trim()
    return ReceivedQuotationApi.list({ offset: { page, limit: LIMIT }, filter })
  }, [page, status, search])

  const { data, isLoading, error, mutate } = useSWR(
    LIST.hasAccess ? `swr.received-quotation.list.${page}.${status}.${search}` : null,
    fetcher,
    { revalidateOnFocus: false },
  )

  const rows: ISalesQuotation[] = data?.rows ?? []
  const count = data?.count ?? 0
  const totalPages = Math.ceil(count / LIMIT)
  const statusOptions = BUYER_QUOTATION_STATUSES.map((s) => ({ value: s, label: t(`status.${s}`) }))
  const from = count === 0 ? 0 : (page - 1) * LIMIT + 1
  const to = Math.min(page * LIMIT, count)
  const clearFilters = () => { setStatus(null); setSearch(""); setPage(1) }

  const header = (
    <>
      <Breadcrumbs separator="/" mb={4}>
        <Anchor component={Link} to="/" fz="var(--nw-fz-body)" c="gray.8">{t("common:home")}</Anchor>
        <Text fz="var(--nw-fz-body)" c="gray.8">{t("list.title")}</Text>
      </Breadcrumbs>
      <Box className={classes.header}>
        <h1 className={classes.title}>{t("list.title")}</h1>
        <Text className={classes.subtitle}>{t("list.subtitle")}</Text>
      </Box>
    </>
  )

  if (!LIST.hasAccess) {
    return (
      <Box>
        {header}
        <DeniedCard permission="PRICING_QUOTATION_LIST" title={t("list.title")} />
      </Box>
    )
  }

  const filtered = !!status || !!search.trim()
  let body
  if (isLoading) body = <ListState state="loading" columns={8} />
  else if (error) body = <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
  else if (rows.length === 0) {
    body = filtered
      ? <ListState state="noResults" action={{ onClick: clearFilters }} />
      : <ListState state="empty" icon={<FileTextIcon size={24} />} title={t("list.empty")} />
  } else {
    body = (
      <TableFrame
        footer={
          <>
            <Text fz="var(--nw-fz-body)" c="gray.8" className={classes.mono}>{from}-{to} / {count}</Text>
            {totalPages > 1 && <Pagination total={totalPages} value={page} onChange={setPage} size="sm" />}
          </>
        }
      >
        <Box className={classes.scroll}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>{t("list.colAction")}</th>
                <th>{t("list.colCode")}</th>
                <th>{t("list.colTitle")}</th>
                <th>{t("list.colSupplier")}</th>
                <th className={classes.num}>{t("list.colTotal")}</th>
                <th>{t("list.colStatus")}</th>
                <th>{t("list.colValidity")}</th>
                <th>{t("list.colDate")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.id} className={classes.row}>
                  <td>
                    <ReasonTooltip reason={GET.hasAccess ? undefined : GET.reason}>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="lg"
                        aria-label={t("list.open")}
                        title={t("list.open")}
                        onClick={() => navigate(`/received-quotation/${item.id}`)}
                      >
                        <EyeIcon size={18} />
                      </ActionIcon>
                    </ReasonTooltip>
                  </td>
                  <td className={classes.mono}>{item.refCode || "-"}</td>
                  <td>{item.title || "-"}</td>
                  <td className={classes.supplier} title={item.supplierBusiness?.profileName || undefined}>{item.supplierBusiness?.profileName || "-"}</td>
                  <td className={`${classes.num} ${classes.mono}`}>{currencyFormat(item.totalAmount || 0)}</td>
                  <td>
                    <DotPill tone={quotationStatusTone(item.status)}>{t(`status.${item.status}`)}</DotPill>
                  </td>
                  <td className={classes.mono}>
                    {item.validUntil ? `${dateFormat(item.validFrom)} ~ ${dateFormat(item.validUntil)}` : "-"}
                  </td>
                  <td className={classes.mono}>{dateFormat(item.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </TableFrame>
    )
  }

  return (
    <Box>
      {header}
      <Flex className={classes.toolbar}>
        <Select
          placeholder={t("list.statusAll")}
          aria-label={t("list.statusFilter")}
          data={statusOptions}
          value={status}
          onChange={(val) => { setStatus(val); setPage(1) }}
          clearable
          w={220}
        />
        <Box flex={1} miw={220} maw={420}>
          <TextInput
            placeholder={t("list.searchPlaceholder")}
            aria-label={t("list.search")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            value={search}
            onChange={(e) => { setSearch(e.currentTarget.value); setPage(1) }}
          />
        </Box>
        <Text className={classes.count}>{t("list.totalCount", { count })}</Text>
      </Flex>
      {body}
    </Box>
  )
}
